import { type PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';
import { type SetSearchParams } from './useExtendSearchParams';
import { LocalSearchParamsContext } from './useGridSearchParams';

/**
 * Хранит параметры локально и предоставляет их вложенным компонентам без изменения URL.
 * @kind component
 */
export function LocalSearchParamsProvider({ children }: PropsWithChildren) {
    const [searchParams, setLocalSearchParams] = useState(() => new URLSearchParams());
    const searchParamsRef = useRef(searchParams);

    const setSearchParams = useCallback<SetSearchParams>((nextSearchParams) => {
        const next =
            typeof nextSearchParams === 'function'
                ? nextSearchParams(new URLSearchParams(searchParamsRef.current))?.[0]
                : nextSearchParams;

        if (!next) {
            return;
        }

        // Обновляем ref сразу: последовательные изменения параметров в одном событии должны видеть предыдущую запись.
        searchParamsRef.current = new URLSearchParams(next);
        setLocalSearchParams(searchParamsRef.current);
    }, []);

    const getSearchParams = useCallback(() => new URLSearchParams(searchParamsRef.current), []);
    const value = useMemo(() => {
        return { searchParams, setSearchParams, getSearchParams };
    }, [searchParams, setSearchParams, getSearchParams]);

    return <LocalSearchParamsContext.Provider value={value}>{children}</LocalSearchParamsContext.Provider>;
}
